import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { BedDouble, Timer, Users2, BadgePercent, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { dashboardApi } from '@/lib/api';
import type { HospitalAdminDashboardData, DeptEfficiency } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

function inr(n: number) { return '₹' + Math.round(n).toLocaleString('en-IN'); }

const DEPT_STATUS_MAP: Record<DeptEfficiency['status'], { label: string; variant: 'success' | 'warning' | 'danger'; icon: React.ElementType }> = {
  good:     { label: 'On Target', variant: 'success', icon: TrendingUp   },
  warning:  { label: 'Lagging',   variant: 'warning', icon: Minus        },
  critical: { label: 'Critical',  variant: 'danger',  icon: TrendingDown },
};

function RadarTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow px-3 py-2 text-xs">
      <p className="font-semibold text-navy">{payload[0]?.payload?.department}</p>
      <p className="text-gray-500">Throughput: <strong>{payload[0]?.value}</strong></p>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function HospitalAdminDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<HospitalAdminDashboardData>({
    queryKey:        ['dashboard-hospital-admin'],
    queryFn:         () => dashboardApi.hospitalAdmin().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  const occupancyPct = Math.round((data.bedOccupancy / data.totalBeds) * 100);
  const occupancyColor = occupancyPct >= 90 ? '#ef4444' : occupancyPct >= 75 ? '#f59e0b' : '#10b981';

  const radarData = data.deptEfficiency.map(d => ({
    department: d.department,
    throughput: d.throughput,
    target: 60,
  }));

  return (
    <DashboardShell
      title="Hospital Admin Dashboard"
      subtitle="Operational overview · department TAT · pending approvals"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard
          icon={BedDouble}
          label="Bed Occupancy"
          value={`${data.bedOccupancy}/${data.totalBeds}`}
          accent="bg-orange/15"
          sub={
            <div className="flex items-center gap-2 mt-0.5">
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${occupancyPct}%`, background: occupancyColor }}/>
              </div>
              <span className="text-2xs font-semibold" style={{ color: occupancyColor }}>{occupancyPct}%</span>
            </div>
          }
        />
        <MetricCard icon={Timer}       label="Avg Operational TAT" value={`${data.avgOperationalTat}m`} accent="bg-blue-50"/>
        <MetricCard icon={Users2}      label="Active Users"        value={data.activeUsers}             accent="bg-violet-50"/>
        <MetricCard icon={BadgePercent} label="Pending Discount Approvals" value={data.pendingDiscounts}
          accent="bg-amber-50" alert={data.pendingDiscounts > 0}
        />
      </MetricsGrid>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Department Efficiency */}
        <SectionCard title="Department Efficiency">
          <ScrollTable>
            <thead>
              <tr>
                <Th>Department</Th><Th right>Avg TAT</Th><Th right>Target</Th>
                <Th right>Throughput</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.deptEfficiency.map(d => {
                const sm = DEPT_STATUS_MAP[d.status];
                const StatusIcon = sm.icon;
                return (
                  <tr key={d.department} className="hover:bg-gray-50 transition">
                    <Td><span className="font-medium">{d.department}</span></Td>
                    <Td right>
                      <span className={clsx(
                        'font-semibold',
                        d.avgTatMinutes > d.targetTatMinutes ? 'text-red-600' : 'text-emerald-600',
                      )}>
                        {d.avgTatMinutes}m
                      </span>
                    </Td>
                    <Td right muted>{d.targetTatMinutes}m</Td>
                    <Td right>{d.throughput}</Td>
                    <Td>
                      <span className={clsx(
                        'inline-flex items-center gap-1 text-2xs font-semibold px-2 py-0.5 rounded-full border',
                        sm.variant === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        sm.variant === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                   'bg-red-50 text-red-700 border-red-200',
                      )}>
                        <StatusIcon size={9}/>{sm.label}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </ScrollTable>
        </SectionCard>

        {/* Throughput Radar */}
        <SectionCard title="Department Throughput Today">
          <div className="px-4 py-2">
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                <PolarGrid stroke="#f3f4f6"/>
                <PolarAngleAxis dataKey="department" tick={{ fontSize: 10, fill: '#6b7280' }}/>
                <Radar name="Throughput" dataKey="throughput" stroke="#E8A020" fill="#E8A020" fillOpacity={0.25} strokeWidth={2}/>
                <Tooltip content={<RadarTooltip/>}/>
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Discount Approvals */}
      {data.discountApprovals.length > 0 && (
        <SectionCard
          title={`Discount Approvals Pending (${data.discountApprovals.length})`}
          action={
            <span className="text-xs text-orange font-semibold">Requires action</span>
          }>
          <ScrollTable>
            <thead>
              <tr>
                <Th>Invoice</Th><Th>Patient</Th><Th right>Original</Th>
                <Th right>Discount</Th><Th>Reason</Th><Th>Requested By</Th><Th>At</Th>
              </tr>
            </thead>
            <tbody>
              {data.discountApprovals.map(d => (
                <tr key={d.id} className="hover:bg-gray-50 transition">
                  <Td><span className="font-mono text-xs text-navy">{d.invoiceNumber}</span></Td>
                  <Td><span className="font-medium">{d.patientName}</span></Td>
                  <Td right>{inr(d.originalAmount)}</Td>
                  <Td right>
                    <span className="font-semibold text-orange">{inr(d.discountAmount)}</span>
                    <span className="text-gray-400 text-2xs ml-1">({Math.round((d.discountAmount / d.originalAmount) * 100)}%)</span>
                  </Td>
                  <Td muted>{d.reason}</Td>
                  <Td muted>{d.requestedBy}</Td>
                  <Td muted>{d.requestedAt}</Td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </SectionCard>
      )}

    </DashboardShell>
  );
}
